import { dayjsWithTimezone } from '@healthhub/react-ui';
import type { ConfigType, Dayjs } from 'dayjs';

import { DATE_FORMAT, DAY_DATE_DISPLAY } from '@shared/constants/constants';

interface SortByDateStringProps {
  ascending?: boolean;
  dateA: string;
  dateB: string;
}

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export const formattedDateDisplay = (date: ConfigType, formatter = DATE_FORMAT) => {
  if (!date) {
    return '';
  }
  if (!formatter) {
    return '';
  }
  const isValidDate = dayjsWithTimezone(date).isValid();

  if (isValidDate) {
    return dayjsWithTimezone(date).format(formatter);
  }

  return '-';
};

export const getDateLabel = (date: string) => {
  const today = dayjsWithTimezone().format(ISO_DATE_FORMAT);
  const tomorrow = dayjsWithTimezone().add(1, 'day').format(ISO_DATE_FORMAT);
  const parsedDate = dayjsWithTimezone(date).format(ISO_DATE_FORMAT);

  if (parsedDate === today) {
    return 'Today';
  }
  if (parsedDate === tomorrow) {
    return 'Tomorrow';
  }
  return dayjsWithTimezone(date).format(DAY_DATE_DISPLAY);
};

export const getDateFromTimeStamp = (time: string) => {
  // time format '21:55:00'
  const d = new Date(); // Creates a Date Object using the clients current time
  const [hours, minutes, seconds] = time.split(':');
  d.setHours(+hours);
  d.setMinutes(+minutes);
  d.setSeconds(+seconds);
  return d;
};

export const isWithInCutOffTime = (startTime: string, endTime: string) => {
  try {
    const startDateTime = getDateFromTimeStamp(startTime);
    const endDateTime = getDateFromTimeStamp(endTime);
    const currentDateTime = new Date();
    if (endDateTime < startDateTime) {
      return currentDateTime >= startDateTime || currentDateTime <= endDateTime;
    }
    return currentDateTime >= startDateTime && currentDateTime <= endDateTime;
  } catch (e) {
    console.warn('An error occurred while checking if current time is within cut off time.', e);
    return false;
  }
};

export const getISODateFormat = (date: ConfigType) => formattedDateDisplay(date, ISO_DATE_FORMAT);

export const dateDiffByMinutes = (startDate: Dayjs, endDate: Dayjs) =>
  dayjsWithTimezone(endDate).diff(startDate, 'minutes');

export const getSubtractDateMonths = (month: number, isFormat = true) =>
  isFormat
    ? dayjsWithTimezone().subtract(month, 'months').format(ISO_DATE_FORMAT)
    : dayjsWithTimezone().subtract(month, 'months');

export const getSubtractDateDays = (days: number, isFormat = true, date?: ConfigType) => {
  const years = days >= 365 ? Math.floor(days / 365) : 0;
  const months = years ? 12 * years : Math.floor(days / 30);
  const remainingDays = years ? days - years * 365 : days - months * 30;

  const result = dayjsWithTimezone(date).subtract(months, 'months').subtract(remainingDays, 'days');

  return isFormat ? result.format(ISO_DATE_FORMAT) : result;
};

export const getCurrentDate = (isFormat = true) =>
  isFormat ? dayjsWithTimezone().format(ISO_DATE_FORMAT) : dayjsWithTimezone();

export const formatDisplayDateSummary = (inputDate: string): string =>
  // time format 21 Jan 2022, 01:15 PM
  dayjsWithTimezone(inputDate).format('DD MMM YYYY, hh:mm A');

export const getDateByMonthNameFormat = (date: Date | null) => {
  if (!date) return null;
  const day = formattedDateDisplay(date, 'DD');
  const month = formattedDateDisplay(date, 'MMM');
  const year = formattedDateDisplay(date, 'YYYY');

  return {
    day,
    month,
    year,
  };
};

export const formatDateToDesiredFormat = (date: ConfigType | string, format = 'DD MMM YYYY') =>
  formattedDateDisplay(date, format);

export const getDayOfWeekByNumber = (dayNumber: number) => {
  if (dayNumber < 0 || dayNumber > 6) return null;

  const shortDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return shortDays[dayNumber];
};

export const getDayOfWeekByDate = (date: string) => dayjsWithTimezone(date).format('ddd');

export const getHourFormat = (date?: string) => (date ? dayjsWithTimezone(date).format('hh:mm A') : null);

export const sortByDateString = ({ ascending, dateA, dateB }: SortByDateStringProps): number => {
  const convertedDateA = dayjsWithTimezone(dateA).valueOf();
  const convertedDateB = dayjsWithTimezone(dateB).valueOf();

  return ascending ? convertedDateA - convertedDateB : convertedDateB - convertedDateA;
};

export const compareIsoDate = (dateA: ConfigType, dateB: ConfigType) =>
  dayjsWithTimezone(dateA).format(ISO_DATE_FORMAT) === dayjsWithTimezone(dateB).format(ISO_DATE_FORMAT);

export const getTimeOfDate = (date: ConfigType) => dayjsWithTimezone(date).format('hh:mm A');

export const getCurrentYear = () => dayjsWithTimezone().year();

export const areInSameYear = (a?: string, b?: string) =>
  !!a && !!b && dayjsWithTimezone(a).year() === dayjsWithTimezone(b).year();

export const isBeforeDate = (date: ConfigType, compareDate: ConfigType) =>
  dayjsWithTimezone(date).isBefore(dayjsWithTimezone(compareDate));

export const isAfterDate = (date: ConfigType, compareDate: ConfigType) =>
  dayjsWithTimezone(date).isAfter(dayjsWithTimezone(compareDate));
